import {
  PRACTICE_NAME_IRRIGATED,
  PRACTICE_NAME_NON_IRRIGATED,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { Paper, Stack, Typography } from "@mui/material";

import { getDefaultHistoricalIrrigationPercentage } from "../../../api-toolkit/useArcPlc";
import { OperatorBox } from "./ArcCoOperatorBox";
import ArcCoPaymentSummaryBox from "./ArcCoPaymentSummaryBox";

const ArcCoCombinedContent = ({ arcCoCalculations, combinedCalculation }) => {
  const irrigatedCalc = arcCoCalculations.find((calc) => calc.practice_name === PRACTICE_NAME_IRRIGATED);
  const nonIrrigatedCalc = arcCoCalculations.find((calc) => calc.practice_name === PRACTICE_NAME_NON_IRRIGATED);
  const hip = getDefaultHistoricalIrrigationPercentage(combinedCalculation?.historical_irrigation_percentage);

  const irrigatedPayment = irrigatedCalc?.arcPayment || 0;
  const nonIrrigatedPayment = nonIrrigatedCalc?.arcPayment || 0;
  const nonIrrigatedPercentage = 1 - hip;
  const weightedIrrigatedPayment = irrigatedPayment * hip;
  const weightedNonIrrigatedPayment = nonIrrigatedPayment * nonIrrigatedPercentage;

  return (
    <Stack className="arc-co-combined-content arc-plc-steps-content" spacing={1.5}>
      {/* Step 1: Show Weighted Individual Results */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 1:</span> Weighted Individual Practice Payments
        </Typography>

        <Stack
          className="arc-co-step-1-stack"
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
          }}
        >
          <ArcCoPaymentSummaryBox title="Irrigated ARC-CO Payment" value={irrigatedPayment} />
          <ArcCoPaymentSummaryBox title="Non-Irrigated ARC-CO Payment" value={nonIrrigatedPayment} />
        </Stack>
      </Paper>

      {/* Step 2: HIP Weighting */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 2:</span> Weight by HIP (Historical Irrigation Percentage)
        </Typography>
        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <ArcCoPaymentSummaryBox title="Irrigated" value={irrigatedPayment} />
          <OperatorBox>×</OperatorBox>
          <ArcCoPaymentSummaryBox title="IRR %" value={hip} formatType="percentage" formatOptions={{ places: 0 }} />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Weighted Irrigated"
            value={weightedIrrigatedPayment}
            formatOptions={{ places: 0 }}
            valueColor="success.main"
          />
        </Stack>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          <ArcCoPaymentSummaryBox title="Non-Irrigated" value={nonIrrigatedPayment} />
          <OperatorBox>×</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="NI %"
            value={nonIrrigatedPercentage}
            formatType="percentage"
            formatOptions={{ places: 0 }}
          />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Weighted Non-Irrigated"
            value={weightedNonIrrigatedPayment}
            formatOptions={{ places: 0 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>

      {/* Step 3: Addition */}
      <Paper sx={{ borderRadius: "0.875rem", boxShadow: "none" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            padding: "14px 18px",
            textAlign: "left",
            "& span": {
              color: "text.secondary",
            },
          }}
        >
          <span>Step 3:</span> Add
        </Typography>

        <Stack
          sx={{
            padding: "20px 18px",
            flexDirection: "row",
            alignItems: "stretch",
            flexWrap: "wrap",
          }}
        >
          <ArcCoPaymentSummaryBox title="Weighted Irrigated" value={weightedIrrigatedPayment} />
          <OperatorBox>+</OperatorBox>
          <ArcCoPaymentSummaryBox title="Weighted Non-Irrigated" value={weightedNonIrrigatedPayment} />
          <OperatorBox>=</OperatorBox>
          <ArcCoPaymentSummaryBox
            title="Total Farm ARC-CO Payment"
            value={combinedCalculation?.arcPayment}
            formatOptions={{ places: 0 }}
            valueColor="success.main"
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ArcCoCombinedContent;
